import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePusher } from 'contexts/PusherProvider';
import { RootState } from 'redux/store';
import {
  SubscriptionCurrentLimit,
  UpdateSubscriptionLimit
} from '../../redux/slices/company';
import {
  companyChannelName,
  SUBSCRIPTION_LIMIT_EVENT
} from 'utils/pusher';

const COMPANY_ROLES = new Set(['company', 'recruiter', 'admin']);
const LIMIT_FIELDS = ['ai_credits', 'jobs', 'candidates'] as const;

const normalizeSubscriptionLimit = (
  data: unknown
): SubscriptionCurrentLimit | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const subscription = payload.subscription;
  const nestedLimit =
    payload.current_limit ??
    (subscription && typeof subscription === 'object'
      ? (subscription as Record<string, unknown>).current_limit
      : undefined);

  if (nestedLimit && typeof nestedLimit === 'object') {
    return nestedLimit as SubscriptionCurrentLimit;
  }

  const flatLimit = LIMIT_FIELDS.reduce<SubscriptionCurrentLimit>((acc, key) => {
    if (key in payload) {
      acc[key] = payload[key] as SubscriptionCurrentLimit[typeof key];
    }
    return acc;
  }, {});

  return Object.keys(flatLimit).length > 0 ? flatLimit : null;
};

/**
 * Subscribes once per authenticated company user session (shell-level).
 * Keeps subscription current_limit in sync when credits or quotas change.
 */
const CompanySubscriptionRealtimeListener = () => {
  const dispatch = useDispatch();
  const pusher = usePusher();
  const user = useSelector((state: RootState) => state.user.user);
  const companies = useSelector((state: RootState) => state.company.companies);
  const companiesRef = useRef(companies);
  const pendingLimitRef = useRef<SubscriptionCurrentLimit | null>(null);

  companiesRef.current = companies;

  useEffect(() => {
    if (!companies || !pendingLimitRef.current) {
      return;
    }

    dispatch(UpdateSubscriptionLimit(pendingLimitRef.current));
    pendingLimitRef.current = null;
  }, [companies, dispatch]);

  useEffect(() => {
    if (!pusher || !user?.company || !COMPANY_ROLES.has(user.role)) {
      return undefined;
    }

    const channelName = companyChannelName(user.company);
    const channel = pusher.subscribe(channelName);

    const onSubscriptionLimit = (data: unknown) => {
      const currentLimit = normalizeSubscriptionLimit(data);
      if (!currentLimit) {
        return;
      }

      if (!companiesRef.current) {
        pendingLimitRef.current = {
          ...pendingLimitRef.current,
          ...currentLimit
        };
        return;
      }

      dispatch(UpdateSubscriptionLimit(currentLimit));
    };

    channel.bind(SUBSCRIPTION_LIMIT_EVENT, onSubscriptionLimit);

    return () => {
      channel.unbind(SUBSCRIPTION_LIMIT_EVENT, onSubscriptionLimit);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, user?.company, user?.role, dispatch]);

  return null;
};

export default CompanySubscriptionRealtimeListener;
